/**
 * The pre-game photosensitivity + camera notice. `renderWarning` mounts an
 * accessible dialog into `root` with a reduced-motion checkbox (seeded from
 * `client/scare/prefs.ts`'s stored override, falling back to the OS
 * `prefers-reduced-motion` signal) and a Start button. The Start click is
 * the user gesture the caller uses to request the camera and unlock audio —
 * `onAccept` fires synchronously from that same click handler, so the
 * caller's own `getUserMedia()`/`AudioContext` calls stay inside the gesture.
 *
 * Accessibility: the panel is `role="dialog"` + `aria-modal="true"`,
 * labelled by the heading and described by the notice paragraph. Initial
 * focus lands on the reduced-motion checkbox (the dialog's first control) so
 * a keyboard/screen-reader user isn't dropped back at the page body; Tab and
 * Shift+Tab are trapped between the checkbox and the Start button via the
 * pure `trapTabKey` (see `focus-trap.ts`) so focus can't escape to the page
 * behind the modal.
 *
 * This is the one place in `client/scare/` that reaches for real browser
 * globals (`window.localStorage`, `window.matchMedia`) rather than taking
 * them injected — everything it depends on (`resolveReducedMotion`, the
 * override read/write) is already pure/injectable, so this stays a thin,
 * untested wiring layer instead of duplicating that logic.
 */

import { trapTabKey } from "./focus-trap";
import {
  readReducedMotionOverride,
  resolveReducedMotion,
  writeReducedMotionOverride,
} from "./prefs";

export type ScareWarningAccept = (reducedMotion: boolean) => void;

const NOTICE_TEXT =
  "This game contains a sudden full-screen jump scare with loud sound and rapid motion. " +
  "The final level's scare is a full-screen video with sound. " +
  "It uses your webcam locally to track your eyes — video never leaves this browser.";

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    // Private-mode storage access can throw just from the property read.
    return undefined;
  }
}

function safeMatchMedia(): ((query: string) => { matches: boolean }) | undefined {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
  return (query: string) => window.matchMedia(query);
}

/** Mounts the warning dialog into `root` and calls `onAccept` once, on the Start click. */
export function renderWarning(root: HTMLElement, onAccept: ScareWarningAccept): void {
  const storage = safeLocalStorage();
  const initialReducedMotion = resolveReducedMotion(
    readReducedMotionOverride(storage),
    safeMatchMedia(),
  );

  const panel = document.createElement("div");
  panel.className = "scare-warning";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-labelledby", "scare-warning-heading");
  panel.setAttribute("aria-describedby", "scare-warning-notice");

  const heading = document.createElement("h2");
  heading.id = "scare-warning-heading";
  heading.textContent = "Before you play";

  const notice = document.createElement("p");
  notice.id = "scare-warning-notice";
  notice.textContent = NOTICE_TEXT;

  const toggleLabel = document.createElement("label");
  toggleLabel.className = "scare-warning-toggle";

  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.id = "scare-reduced-motion";
  checkbox.checked = initialReducedMotion;

  const toggleText = document.createElement("span");
  toggleText.textContent = "Reduced motion";

  toggleLabel.appendChild(checkbox);
  toggleLabel.appendChild(toggleText);

  const startButton = document.createElement("button");
  startButton.type = "button";
  startButton.textContent = "Start";
  startButton.addEventListener("click", () => {
    const reducedMotion = checkbox.checked;
    writeReducedMotionOverride(storage, reducedMotion);
    onAccept(reducedMotion);
  });

  panel.addEventListener("keydown", (event) => {
    trapTabKey(event, checkbox, startButton, document.activeElement);
  });

  panel.appendChild(heading);
  panel.appendChild(notice);
  panel.appendChild(toggleLabel);
  panel.appendChild(startButton);
  root.appendChild(panel);

  checkbox.focus();
}
